// All help copy of the connector workspace, one entry per HelpPopover: what it does, examples
// ({ label, value }) and one watch-out. Generic examples only.
export const HELP = {
  url: {
    label: 'URL',
    what: "Where the request goes. Put the agent's arguments and the shopper's details in it as placeholders (type {{); they are filled and URL-encoded on our server, and the model never sees this URL.",
    examples: [
      {
        label: 'GET https://api.example.com/availability?city={{args.city}}&date={{args.date}}',
        value: { method: 'GET', url: 'https://api.example.com/availability?city={{args.city}}&date={{args.date}}' },
      },
      {
        label: 'GET https://api.example.com/items/{{args.item_code}}',
        value: { method: 'GET', url: 'https://api.example.com/items/{{args.item_code}}' },
      },
      {
        label: 'GET https://api.example.com/customers/{{identity.email}}/bookings',
        value: { method: 'GET', url: 'https://api.example.com/customers/{{identity.email}}/bookings' },
      },
      {
        label: 'POST https://api.example.com/search  body {"query": "{{args.query}}"}',
        value: { method: 'POST', url: 'https://api.example.com/search', body: '{"query": "{{args.query}}"}' },
      },
    ],
    watchOut:
      'https and a public host only. An example adds the arguments it needs: give each a description and a test value.',
  },
  args: {
    label: 'Arguments',
    what: 'What the agent passes in when it calls the tool, and the shopper fields the request reads. Each argument has a description the model reads and a test value for Send.',
    watchOut:
      'Up to 6. A name like order_id reads as a shopper identifier and is refused unless the tool is personal; use order.',
  },
  argDescription: {
    label: 'Argument description',
    what: 'Tells the model what to put in this argument. Say the format and give one example value.',
  },
  auth: {
    label: 'Secret',
    what: 'The API key or token, stored once under Settings → Secrets and added to every request. Nobody sees it again, the agent included.',
    watchOut: 'Never paste a token into Headers or the URL.',
  },
  root: {
    label: 'Rows',
    what: 'The list in the response that holds the rows the agent gets, one per item, slot or order. Each [] opens one list level (at most two).',
    examples: [
      { label: 'data.items', value: 'data.items' },
      { label: 'results', value: 'results' },
      { label: 'items[].options[]', value: 'items[].options[]' },
    ],
    watchOut: 'Blank reads the whole body as the rows.',
  },
  mapping: {
    label: 'Mapping syntax',
    what: 'Each row field reads one response path. ^.field reads the parent row under a [] root; $sum(path, field, from, days) adds a value over dated keys; $key is a dictionary key.',
    examples: [
      { label: 'place ← venue.name', value: { key: 'place', value: 'venue.name' } },
      { label: 'item ← ^.name', value: { key: 'item', value: '^.name' } },
      {
        label: 'total ← $sum(days, price, {{args.from}}, {{args.days}})',
        value: { key: 'total', value: '$sum(days, price, {{args.from}}, {{args.days}})' },
      },
      { label: 'code ← $key', value: { key: 'code', value: '$key' } },
    ],
    watchOut: 'Only mapped fields reach the agent. Leave out internal notes and personal data.',
  },
  rules: {
    label: 'Outcome rules',
    what: 'When a call counts as not found or failed, and which rows to keep. A filter compares a response path with a value or an argument, case-insensitively.',
    examples: [
      { label: 'Filter city equals {{args.city}}', value: { key: 'city', value: '{{args.city}}' } },
      { label: 'Filter size equals {{args.size}}', value: { key: 'size', value: '{{args.size}}' } },
    ],
  },
  strict: {
    label: 'Strict',
    what: 'What happens when a filter matches no row. Off: every row comes back. On: no rows, and the not-found text.',
    watchOut: 'Leave it off where spelling may differ, so a near match still finds something.',
  },
  description: {
    label: 'Description',
    what: 'Tells the agent when to use this tool: what it answers and the situation the shopper is in.',
    examples: [
      {
        label: 'Free appointment slots by city and date. Use when the shopper asks when they can book.',
        value: 'Free appointment slots by city and date. Use when the shopper asks when they can book.',
      },
      {
        label: 'Stock of an item by city. Use when the shopper asks where an item is available.',
        value: 'Stock of an item by city. Use when the shopper asks where an item is available.',
      },
    ],
    watchOut: 'Too vague, such as "Calls the API", and the agent will not know when to call it.',
  },
  guidance: {
    label: 'Guidance',
    what: 'How the agent reads the rows and presents them. One instruction per line works best.',
    examples: [
      {
        label: 'One row per slot. Show place, time and price.',
        value: 'One row per slot. Show place, time and price.',
      },
      { label: 'If nothing fits, suggest the next day, once.', value: 'If nothing fits, suggest the next day, once.' },
    ],
  },
  notFoundText: {
    label: 'Not-found text',
    what: 'What the agent hears when the call finds nothing. Short and factual; the agent rephrases it.',
    examples: [
      { label: 'Nothing free for these criteria.', value: 'Nothing free for these criteria.' },
      { label: 'No item found with this code.', value: 'No item found with this code.' },
    ],
  },
  statusText: {
    label: 'Status while it runs',
    what: "The line the shopper sees while this tool runs, next to the dots. Blank shows the general 'Checking the latest details'.",
    examples: [
      { label: 'Checking availability', value: 'Checking availability' },
      { label: 'Checking prices for your dates', value: 'Checking prices for your dates' },
      { label: 'Looking up the offer', value: 'Looking up the offer' },
    ],
    watchOut: "Write it in the bot's language: it is shown exactly as typed, at most 40 characters.",
  },
  personal: {
    label: 'Personal',
    what: 'On when the tool answers about the signed-in shopper, such as their bookings. It then runs only for identified shoppers.',
    watchOut: 'Needs {{identity.email}}, {{identity.phone}} or {{identity.profileId}} in the URL or body.',
  },
  maxTextChars: {
    label: 'Text per field',
    what: 'The longest text the agent gets from one field of a row; longer text is cut. Blank keeps 300.',
    examples: [
      { label: '300', value: 300 },
      { label: '1500', value: 1500 },
      { label: '3000', value: 3000 },
    ],
    watchOut:
      'Long text on many rows fills the result limit and later rows are dropped; use it on tools that return one or a few rows.',
  },
  agentSees: {
    label: 'What the agent sees',
    what: 'Exactly what reaches the model after the last Send: only the mapped fields, at most the max rows, with the guidance. The meter shows the size against the 12,000-character limit.',
    watchOut: 'Too big? Remove fields the agent never uses, or lower Max rows.',
  },
  picker: {
    label: 'Picking from the response',
    what: 'Use as rows on a list makes each item a row; + field on a value adds it to every row. A list inside a list gives a [] root, and a value of the outer item reads as ^.',
    examples: [
      { label: 'data.items', value: 'data.items' },
      { label: 'items[].options[]', value: 'items[].options[]' },
    ],
    watchOut: 'Only the first few items of each list are shown; the call reads them all.',
  },
};
